import React, { ReactElement, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Input } from '../Input';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import classnames from 'classnames';
import { AutocompleteProps, OptionItem } from './Autocomplete.types';
import { OptionList } from '../OptionList';

const ARROW_UP = 38;
const ARROW_DOWN = 40;
const ENTER = 13;

const InputContainer = styled.div`
  position: relative;
  width: 100%;
`;

const Autocomplete = <T extends OptionItem>(props: AutocompleteProps<T>) => {
  const {
    onSelect,
    inputProps,
    onChange,
    disableAutocomplete,
    options,
    formatter = v => v,
  } = props;
  const value = formatter(inputProps.value || '');
  const [suggestions, setSuggestions] = useState<T[]>([]);
  const [canSeeOptions, setCanSeeOptions] = useState<boolean>(false);
  // Added cursor to track current position of the button
  const [cursor, setCursor] = useState<number>(0);
  const ref = useRef<HTMLDivElement | null>(null);

  const handleClickOutside = useCallback(() => {
    setCanSeeOptions(false);
    setSuggestions([]);
  }, [setCanSeeOptions]);

  useOnClickOutside(ref, handleClickOutside);

  const filterData = (value: string) => {
    return options
      ? options.filter(item => {
          return item.key.toLowerCase().includes(value.toLowerCase());
        })
      : [];
  };

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const suggestionData = filterData(event.target.value);
    setSuggestions(suggestionData);
    onChange(event.target.value);
    // only show the options if the autocomplete is not disabled
    setCanSeeOptions(!disableAutocomplete);
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.keyCode === ARROW_DOWN) {
      setCursor(pointer => (pointer === suggestions.length ? 1 : pointer + 1));
    }

    if (event.keyCode === ARROW_UP) {
      setCursor(pointer => (pointer === 1 ? suggestions.length : pointer - 1));
    }

    if (event.keyCode === ENTER) {
      event.preventDefault();
      const suggestion = suggestions[cursor - 1];
      if (suggestion) {
        handleSelect(suggestion)();
      }
    }
  };

  const handleSelect =
    (suggestion: T): (() => void) =>
    () => {
      onSelect(suggestion);
      setSuggestions([]);
      setCanSeeOptions(false);
      setCursor(0);
    };

  const renderSuggestions = (): ReactElement[] => {
    return suggestions.length
      ? suggestions.map((suggestion, index) => {
          return (
            <li
              key={suggestion.key}
              onClick={handleSelect(suggestion)}
              className={classnames('', {
                active: cursor - 1 === index,
              })}
            >
              {suggestion.label}
            </li>
          );
        })
      : [
          <li key={'autocomplete-not-found-li-key'}>
            Your search was not found.
          </li>,
        ];
  };

  return (
    <InputContainer ref={ref}>
      <Input
        value={value}
        onChange={handleInput}
        // So that browsers don't show an autofill dialog on top of the address select dropdown
        autoComplete="off"
        name={inputProps.name}
        className={inputProps.className}
        id={inputProps.id}
        data-testid={inputProps.id}
        label={inputProps.label}
        icon={inputProps.icon}
        tooltip={inputProps.tooltip}
        onFocus={e => {
          handleInput(e);
          inputProps.onFocus!(e);
        }}
        onBlur={inputProps.onBlur}
        active={inputProps.active}
        onKeyDown={handleKeyDown}
      />
      {canSeeOptions && (
        <OptionList data-label={inputProps.label}>{renderSuggestions()}</OptionList>
      )}
    </InputContainer>
  );
};

export default Autocomplete;
